import axiosInstance from '../utils/axios';

/**
 * 감사로그 모니터링 API 서비스
 */
const auditLogService = {
  /**
   * 감사로그 목록 조회 (필터링 및 페이징)
   * @param {Object} filters 필터 조건
   * @param {number} page 페이지 번호
   * @param {number} limit 페이지당 개수
   * @returns {Promise<Object>} 감사로그 목록
   */
  async getAuditLogs(filters = {}, page = 1, limit = 20) {
    try {
      const params = {
        page,
        limit,
        ...filters
      };
      
      const response = await axiosInstance.get('/audit/logs', { params });
      
      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
          pagination: response.data.pagination
        };
      }
      
      return {
        success: false,
        message: response.data.message || '감사로그 목록을 불러오는데 실패했습니다.'
      };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || '감사로그 목록을 불러오는 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  },

  /**
   * 감사로그 상세 조회
   * @param {number} id 감사로그 ID
   * @returns {Promise<Object>} 감사로그 상세
   */
  async getAuditLogDetail(id) {
    try {
      const response = await axiosInstance.get(`/audit/logs/${id}`);
      
      if (response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      }
      
      return {
        success: false,
        message: response.data.message || '감사로그 상세 정보를 불러오는데 실패했습니다.'
      };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || '감사로그 상세 정보를 불러오는 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  },

  /**
   * 카테고리별 통계 조회
   * @param {Object} filters 필터 조건
   * @returns {Promise<Object>} 카테고리별 통계
   */
  async getCategoryStats(filters = {}) {
    try {
      const response = await axiosInstance.get('/audit/stats/category', {
        params: filters
      });
      
      if (response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      }
      
      return {
        success: false,
        message: response.data.message || '카테고리 통계를 불러오는데 실패했습니다.'
      };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || '카테고리 통계를 불러오는 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  },

  /**
   * 사용자별 활동 통계 조회
   * @param {string|null} userId 사용자 ID (null이면 전체)
   * @param {Object} filters 필터 조건
   * @returns {Promise<Object>} 사용자 활동 통계
   */
  async getUserActivityStats(userId = null, filters = {}) {
    try {
      const params = { ...filters };
      if (userId) {
        params.user_id = userId;
      }
      
      const response = await axiosInstance.get('/audit/stats/user', { params });
      
      if (response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      }
      
      return {
        success: false,
        message: response.data.message || '사용자 활동 통계를 불러오는데 실패했습니다.'
      };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || '사용자 활동 통계를 불러오는 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  },

  /**
   * 시간대별 통계 조회
   * @param {string} interval 시간 간격 (hour, day, week, month)
   * @param {Object} filters 필터 조건
   * @returns {Promise<Object>} 시간대별 통계
   */
  async getTimelineStats(interval = 'day', filters = {}) {
    try {
      const params = {
        interval,
        ...filters
      };
      
      const response = await axiosInstance.get('/audit/stats/timeline', { params });
      
      if (response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      }
      
      return {
        success: false,
        message: response.data.message || '시간대별 통계를 불러오는데 실패했습니다.'
      };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || '시간대별 통계를 불러오는 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  },

  /**
   * 실시간 보안 이벤트 조회
   * @param {number} minutes 최근 N분 (기본값: 15)
   * @returns {Promise<Object>} 실시간 보안 이벤트
   */
  async getRealtimeSecurityEvents(minutes = 15) {
    try {
      const response = await axiosInstance.get('/audit/events/realtime', {
        params: { minutes }
      });
      
      if (response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      }
      
      return {
        success: false,
        message: response.data.message || '실시간 보안 이벤트를 불러오는데 실패했습니다.'
      };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || '실시간 보안 이벤트를 불러오는 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  },

  /**
   * 감사로그 엑셀 다운로드
   * @param {Object} filters 필터 조건
   * @returns {Promise<Blob>} 엑셀 파일
   */
  async exportToExcel(filters = {}) {
    try {
      const response = await axiosInstance.post('/audit/export/excel', 
        { filters },
        {
          responseType: 'blob'
        }
      );
      
      // JSON 에러 응답인지 확인
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        return {
          success: false,
          message: errorData.message || '엑셀 다운로드 중 오류가 발생했습니다.'
        };
      }
      
      // Blob으로 파일 다운로드
      const blob = new Blob([response.data], {
        type: 'text/csv; charset=utf-8'
      });
      
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
      
      return {
        success: true,
        message: 'CSV 파일 다운로드가 시작되었습니다.'
      };
    } catch (error) {
      // blob 응답에서 에러 메시지 추출 시도
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          return {
            success: false,
            message: errorData.message || '엑셀 다운로드 중 오류가 발생했습니다.'
          };
        } catch (parseError) {
          // JSON 파싱 실패 시 기본 에러 메시지 반환
        }
      }
      
      const errorMessage = error.response?.data?.message || error.message || '엑셀 다운로드 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  }
};

export default auditLogService;
